<script setup lang="ts">
import type { ActiveScratchCard } from '~/composables/useScratchCard'

/**
 * The Dashboard's "Preview on shop" for a scratch card:
 * `/{locale}/preview/scratch-card?ruleId=<id>[&step=…][&audience=account]`.
 *
 * `scratchCardPreview` serves the rule's card whatever its status or window, so
 * a paused campaign, one that has not started, one at its prize cap and one
 * whose prize is out of stock all open here exactly as they will on the shop.
 * Nothing is sent, stored or reported: the flow's preview mode short-circuits
 * every write, and E's preview bar is the navigation between the screens.
 *
 * `missing` is therefore NOT "the card is off" — it means an unknown or
 * other-channel `ruleId`, or one of the two configuration errors that survive
 * the preview's stock override (no GIFT item, or a VALUE rule with no threshold
 * in this channel's currency; the Dashboard flags both as NO_GIFT_OPTIONS /
 * NO_THRESHOLD_IN_CURRENCY). A thrown request lands here too, with the console
 * error below as its only trace.
 *
 * `useScratchFlowCore`, not `useScratchFlow`: this page is small enough that the
 * build folds it into the entry chunk of every route, so anything it imports is
 * shipped to every visitor. Opening the preview and reading `state` is all it
 * needs; the step machine arrives with the overlay, as it does on the shop.
 */
definePageMeta({ layout: false })

const route = useRoute()
const { t } = useI18n()
const flow = useScratchFlowCore()

const STEPS = ['SCRATCH', 'REVEAL', 'EMAIL', 'PHONE', 'SUCCESS'] as const

const first = (value: unknown) => (Array.isArray(value) ? value[0] : value) as string | undefined

const ruleId = computed(() => first(route.query.ruleId)?.trim() ?? '')
const step = computed(() => STEPS.find(candidate => candidate === first(route.query.step)?.toUpperCase()) ?? 'SCRATCH')
const skipEmail = computed(() => first(route.query.audience) === 'account')

const card = shallowRef<ActiveScratchCard | null>(null)
const status = ref<'loading' | 'ready' | 'missing'>('loading')

function open(): void {
  if (card.value)
    flow.openPreview(card.value, { step: step.value, skipEmail: skipEmail.value })
}

onMounted(async () => {
  if (!ruleId.value) {
    status.value = 'missing'
    return
  }
  try {
    const { data } = await useGraphqlQuery('scratchCardPreview', { ruleId: ruleId.value })
    card.value = (data?.scratchCardPreview ?? null) as ActiveScratchCard | null
  }
  catch (error) {
    console.error('scratchCardPreview failed', error)
  }
  status.value = card.value ? 'ready' : 'missing'
  open()
})

// Same belt as pages/preview/homepage.vue (no useSeo: it would emit
// canonical/hreflang). The response also carries `no-store` and `X-Robots-Tag`
// from the `/*/preview/**` route rule in nuxt.config.ts.
useSeoMeta({
  title: 'Preview — Scratch card',
  robots: 'noindex, nofollow, noarchive, nosnippet',
})
useHead({ meta: [{ name: 'referrer', content: 'no-referrer' }] })
</script>

<template>
  <div class="bg-[var(--page)] px-6 py-16 text-center text-sm text-[var(--ink)] min-h-dvh">
    <p v-if="status === 'missing'" class="m-0">
      {{ t('scratch.preview.not_found') }}
    </p>
    <button
      v-else-if="status === 'ready' && !flow.state.value.open"
      type="button"
      class="cursor-pointer border border-[var(--ink)] rounded-full bg-[var(--ink)] px-6 py-3 text-sm text-white tracking-[0.04em]"
      @click="open()"
    >
      {{ t('scratch.preview.reopen') }}
    </button>
  </div>
</template>
